import React, { useState, useRef, useEffect } from "react";
import { Box, Button, IconButton, InputAdornment, Paper, Typography } from "@mui/material";
import {
	MailOutline,
	ArrowForward,
	ArrowBack,
	LockOutlined,
	LockReset,
	Visibility,
	VisibilityOff,
	CheckCircle,
	RadioButtonUnchecked,
	Check,
} from "@mui/icons-material";
import styles from "./styles.module.scss";
import { NotistackServices, UserServices } from "shared/services";
import { Link, OTPInput, Countdown, CustomTextField } from "components";
import { useNavigate } from "react-router-dom";

type Stage = "EMAIL" | "OTP" | "NEW_PASSWORD" | "SUCCESS";

export const ForgotPasswordPage = () => {
	const navigate = useNavigate();
	const [activeStage, setActiveStage] = useState<Stage>("EMAIL");
	const [loading, setLoading] = useState(false);

	// States for EMAIL stage
	const [email, setEmail] = useState("");

	// States for OTP stage
	const [otp, setOtp] = useState<string[]>(new Array(6).fill(""));
	const [canResend, setCanResend] = useState(false);
	const [targetDate, setTargetDate] = useState<Date>(new Date(Date.now() + 59000));
	const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

	// States for NEW_PASSWORD stage
	const [resetToken, setResetToken] = useState("");
	const [newPassword, setNewPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [showNewPassword, setShowNewPassword] = useState(false);
	const [showConfirmPassword, setShowConfirmPassword] = useState(false);

	const handleEmailSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (!email) {
			NotistackServices.showErrorMessage("Por favor, informe seu e-mail.");
			return;
		}

		setLoading(true);
		try {
			await UserServices.forgotPassword(email);
			NotistackServices.showSuccessMessage("Código enviado com sucesso!");
			setActiveStage("OTP");
			setTargetDate(new Date(Date.now() + 59000));
			setCanResend(false);
		} catch (error: any) {
			// O tratamento de erro global pode já ter exibido algo, mas garantimos aqui
			const msg = error?.response?.data?.message || error.message || "Erro ao enviar código.";
			NotistackServices.showErrorMessage(msg);
		} finally {
			setLoading(false);
		}
	};

	const handleResend = async () => {
		setLoading(true);
		try {
			await UserServices.forgotPassword(email);
			NotistackServices.showSuccessMessage("Código reenviado!");
			setTargetDate(new Date(Date.now() + 59000));
			setCanResend(false);
		} catch (error: any) {
			const msg = error?.response?.data?.message || error.message || "Erro ao reenviar código.";
			NotistackServices.showErrorMessage(msg);
		} finally {
			setLoading(false);
		}
	};

	const handleOtpSubmit = async (event: React.FormEvent) => {
		event.preventDefault();
		const code = otp.join("");
		if (code.length < 6) {
			NotistackServices.showErrorMessage("Por favor, preencha o código completo.");
			return;
		}

		setLoading(true);
		try {
			const token = await UserServices.verifyRecoveryCode(email, code);
			setResetToken(token);
			NotistackServices.showSuccessMessage("Código validado com sucesso!");
			setActiveStage("NEW_PASSWORD");
		} catch (error: any) {
			const msg = error?.response?.data?.message || error.message || "Código inválido ou expirado.";
			NotistackServices.showErrorMessage(msg);
		} finally {
			setLoading(false);
		}
	};

	const handleNewPasswordSubmit = async (event: React.FormEvent) => {
		event.preventDefault();

		if (!newPassword || !confirmPassword) {
			NotistackServices.showErrorMessage("Preencha todos os campos.");
			return;
		}

		if (newPassword !== confirmPassword) {
			NotistackServices.showErrorMessage("As senhas não conferem.");
			return;
		}

		if (newPassword.length < 8 || !/[A-Z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
			NotistackServices.showErrorMessage("A senha não atende aos requisitos.");
			return;
		}

		setLoading(true);
		try {
			await UserServices.confirmPasswordReset(resetToken, newPassword);
			NotistackServices.showSuccessMessage("Senha redefinida com sucesso!");
			setActiveStage("SUCCESS");
		} catch (error: any) {
			const msg = error?.response?.data?.message || error.message || "Erro ao redefinir a senha.";
			NotistackServices.showErrorMessage(msg);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (activeStage === "OTP") {
			inputRefs.current[0]?.focus();
		}
	}, [activeStage]);

	// Validation checks for UI feedback
	const isMinLength = newPassword.length >= 8;
	const hasUpperCase = /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword);
	const hasNumber = /[0-9]/.test(newPassword);

	// Render SUCCESS Stage
	if (activeStage === "SUCCESS") {
		return (
			<Paper
				elevation={0}
				className={styles.card}
			>
				<div
					className={styles.iconContainer}
					style={{ marginBottom: "32px" }}
				>
					<div className={`${styles.iconCircle} ${styles.success}`}>
						<div
							className={styles.blurEffect}
							style={{ transform: "scale(1.5)", opacity: 0.5 }}
						/>
						<Check className={styles.icon} />
					</div>
				</div>

				<Box sx={{ textAlign: "center", mb: 4 }}>
					<Typography
						variant="h5"
						sx={{ fontWeight: "bold", mb: 2, color: "text.primary" }}
					>
						Senha Alterada com Sucesso!
					</Typography>

					<Typography
						variant="body2"
						sx={{ color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.6, px: 2 }}
					>
						Sua conta está segura novamente. Você já pode acessar a plataforma com sua nova senha.
					</Typography>
				</Box>

				<Button
					fullWidth
					className={styles.submitButton}
					onClick={() => navigate("/auth/login")}
					endIcon={<ArrowForward />}
				>
					Ir para o Dashboard
				</Button>
			</Paper>
		);
	}

	// Render NEW_PASSWORD Stage
	if (activeStage === "NEW_PASSWORD") {
		return (
			<Paper
				elevation={0}
				className={styles.card}
			>
				<div style={{ textAlign: "center", marginBottom: "24px" }}>
					<Typography
						variant="h6"
						sx={{ fontWeight: "bold", color: "text.primary" }}
					>
						Crie uma nova senha
					</Typography>
				</div>

				<form
					noValidate
					onSubmit={handleNewPasswordSubmit}
				>
					<CustomTextField
						label="Nova Senha"
						fullWidth
						type={showNewPassword ? "text" : "password"}
						variant="outlined"
						placeholder="••••••••"
						disabled={loading}
						value={newPassword}
						onChange={(e) => setNewPassword(e.target.value)}
						error={newPassword !== "" && !(isMinLength && hasUpperCase && hasNumber)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<LockOutlined sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={() => setShowNewPassword(!showNewPassword)}
										edge="end"
										sx={{ color: "text.secondary" }}
									>
										{showNewPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>

					<div className={styles.requirementsList}>
						<div className={styles.requirementsTitle}>Requisitos:</div>
						<ul>
							<li className={isMinLength ? styles.valid : ""}>
								{isMinLength ? <CheckCircle sx={{ fontSize: 16 }} /> : <RadioButtonUnchecked sx={{ fontSize: 16 }} />}
								Mínimo de 8 caracteres
							</li>
							<li className={hasUpperCase ? styles.valid : ""}>
								{hasUpperCase ? <CheckCircle sx={{ fontSize: 16 }} /> : <RadioButtonUnchecked sx={{ fontSize: 16 }} />}
								Uma letra maiúscula/minúscula
							</li>
							<li className={hasNumber ? styles.valid : ""}>
								{hasNumber ? <CheckCircle sx={{ fontSize: 16 }} /> : <RadioButtonUnchecked sx={{ fontSize: 16 }} />}
								Um número
							</li>
						</ul>
					</div>

					<CustomTextField
						label="Confirmar Nova Senha"
						fullWidth
						type={showConfirmPassword ? "text" : "password"}
						variant="outlined"
						placeholder="••••••••"
						disabled={loading}
						value={confirmPassword}
						onChange={(e) => setConfirmPassword(e.target.value)}
						error={confirmPassword !== "" && newPassword !== confirmPassword}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<LockReset sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={() => setShowConfirmPassword(!showConfirmPassword)}
										edge="end"
										sx={{ color: "text.secondary" }}
									>
										{showConfirmPassword ? <VisibilityOff /> : <Visibility />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>

					<Button
						type="submit"
						fullWidth
						className={styles.submitButton}
						disabled={loading || !(isMinLength && hasUpperCase && hasNumber && newPassword === confirmPassword)}
					>
						Redefinir Senha e Entrar
					</Button>
				</form>
			</Paper>
		);
	}

	// Render OTP Stage
	if (activeStage === "OTP") {
		return (
			<Paper
				elevation={0}
				className={styles.card}
			>
				<div className={styles.iconContainer}>
					<div className={styles.iconCircle}>
						<div className={styles.blurEffect} />
						<MailOutline className={styles.icon} />
					</div>
				</div>

				<Box sx={{ textAlign: "center", mb: 4 }}>
					<Typography
						variant="h5"
						sx={{ fontWeight: "bold", mb: 1 }}
					>
						Verifique seu e-mail
					</Typography>

					<Typography
						variant="body2"
						sx={{ color: "text.secondary", fontSize: "0.875rem", lineHeight: 1.6 }}
					>
						Enviamos um código de 6 dígitos para o e-mail
						<Box
							component="span"
							sx={{ display: "block", color: "text.primary", fontWeight: 500, mt: 0.5 }}
						>
							{email || "usuario@exemplo.com"}
						</Box>
					</Typography>
				</Box>

				<form
					noValidate
					onSubmit={handleOtpSubmit}
				>
					<OTPInput
						value={otp}
						onChange={setOtp}
						disabled={loading}
					/>

					<div className={styles.timerText}>
						{canResend ? (
							<span
								onClick={handleResend}
								style={{
									cursor: "pointer",
									textDecoration: "underline",
									color: "var(--mui-palette-primary-main)",
								}}
							>
								Reenviar código
							</span>
						) : (
							<>
								Reenviar código em{" "}
								<Countdown
									date={targetDate}
									className={styles.time}
									onComplete={() => setCanResend(true)}
								/>
							</>
						)}
					</div>

					<Button
						type="submit"
						fullWidth
						className={styles.submitButton}
						disabled={loading}
						endIcon={<ArrowForward />}
					>
						Validar Código
					</Button>
				</form>

				<div className={styles.backLinkContainer}>
					<Link
						href="./login"
						underline="none"
						sx={{
							"fontSize": "0.875rem",
							"fontWeight": 500,
							"color": "text.secondary",
							"transition": "color 0.2s",
							"&:hover": {
								color: "text.primary",
							},
						}}
						preserveQueryParams
						className={styles.backLink}
					>
						<ArrowBack sx={{ fontSize: 16 }} />
						Voltar ao login
					</Link>
				</div>
			</Paper>
		);
	}

	// Render Email Stage
	return (
		<>
			<Paper
				elevation={0}
				className={styles.card}
			>
				<Typography
					variant="body2"
					sx={{
						color: "text.secondary",
						mb: 3,
						fontSize: "0.875rem",
						lineHeight: 1.6,
					}}
				>
					Digite seu e-mail para receber as instruções de redefinição de senha.
				</Typography>
				<form
					noValidate
					onSubmit={handleEmailSubmit}
				>
					<CustomTextField
						id="email"
						label="Email"
						fullWidth
						variant="outlined"
						placeholder="exemplo@empresa.com"
						disabled={loading}
						autoComplete="email"
						value={email}
						onChange={(e) => setEmail(e.target.value)}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<MailOutline sx={{ color: "text.secondary" }} />
								</InputAdornment>
							),
						}}
					/>

					<Button
						type="submit"
						fullWidth
						className={styles.submitButton}
						disabled={loading}
					>
						Enviar Código de Recuperação
					</Button>
				</form>
			</Paper>
			<Box sx={{ textAlign: "center", mt: 4 }}>
				<Link
					href="./login"
					underline="none"
					sx={{
						"fontSize": "0.875rem",
						"fontWeight": 500,
						"color": "text.secondary",
						"transition": "color 0.2s",
						"&:hover": {
							color: "text.primary",
						},
					}}
					preserveQueryParams
				>
					Voltar para o Login
				</Link>
			</Box>
		</>
	);
};
